import { useState, useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Link,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { dataConstants } from '../../../../constants';
import { roundNum } from '../../../../helpers';
import { addCommas } from '../../../../utils';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';

const { SORT_DIR } = dataConstants || {};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2a2d64',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2a2d64',
    fontWeight: 600,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    padding: '7px 15px 5px 15px',
  },
}));

const AntTabs = styled(Tabs)(() => ({
  border: '1px solid #e8e8e8',
  borderBottom: 'transparent',
  borderRadius: '8px 8px 0 0',
  background: '#fbfbfc',
  '& .MuiTabs-indicator': {
    backgroundColor: '#3a3a6f',
  },
  '& .MuiTabs-scrollButtons': {
    '&.Mui-disabled': { opacity: 0.3 },
  },
}));

const AntTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0,
  },
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  color: 'rgba(0, 0, 0, 0.85)',
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': {
    color: '#161b57',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: 'primary.main',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff',
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 0
      : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgb(19, 47, 76)'
      : '1px solid #eae9ee',
  borderRadius: '7px',
  [theme.breakpoints.down('md')]: {
    borderRadius: '0 0 7px 7px',
  },
  '& .Item-Head': {
    background: '#fbfbfc',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: theme.spacing(1.5, 2, 1.5, 2),
    borderRadius: '7px 7px 0 0',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 600,
      color: '#13113c',
      fontSize: '.875rem',
    },
  },
  '& .Item-Body': {
    padding: theme.spacing(1),
  },
}));

const BuyAddRedSold = ({ addTable, holdingsTbl, top_text, stockTickers, name }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedTab, setSelectedTab] = useState(0);

  const handleChange = useCallback((event, newValue) => {
    setSelectedTab(newValue);
  }, []);

  const safeHoldings = useMemo(
    () => (Array.isArray(holdingsTbl) ? holdingsTbl : []),
    [holdingsTbl],
  );

  const safeTickers = useMemo(
    () => (Array.isArray(stockTickers) ? stockTickers : []),
    [stockTickers],
  );

  const renderTableContent = useCallback(
    (tbls) => {
      const filtered = safeHoldings.filter((type) => type.changeType === tbls.filterType);
      const addTableBody =
        filtered.length > 0
          ? [...filtered]
              .sort((a, b) =>
                tbls.dir === SORT_DIR?.DESC
                  ? b[tbls.sortField] - a[tbls.sortField]
                  : a[tbls.sortField] - b[tbls.sortField],
              )
              .slice(0, 8)
          : null;

      return (
        <Item
          component="div"
          className="py-2"
          sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
        >
          <TableContainer>
            <Box component="div" className="option-title my-1 py-0">
              <Typography
                className="Item-Head"
                variant="h3"
                sx={{
                  color: '#333',
                  fontWeight: 700,
                  fontSize: '1rem',
                  display: { md: 'block', sm: 'none', xs: 'none' },
                }}
              >
                {tbls.tableName}
              </Typography>
            </Box>
            <Box component="div" className="option-content">
              {addTableBody ? (
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    '& th': { color: '#666' },
                    '& .th-1': { textAlign: 'right' },
                    '& th:nth-of-type(1), td:nth-of-type(1)': { pr: 0 },
                    '& th:nth-of-type(2), td:nth-of-type(2)': { pl: 0 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {tbls.cols.map((c, i) => (
                        <TableCell
                          key={c.field}
                          sx={{ fontSize: '.9rem', bgcolor: '#fbfbfc', whiteSpace: 'nowrap' }}
                          align={i === 0 ? 'left' : 'right'}
                        >
                          {c.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{
                      '& .MuiTableRow-root:nth-last-of-type(1) td': { borderBottom: 0 },
                      '& th': { whiteSpace: 'nowrap' },
                      '& td:nth-of-type(1)': { pr: 0 },
                      '& td:nth-of-type(2)': { pl: 0 },
                      '& .nameOfIssuer': {
                        cursor: 'pointer',
                        color: 'text.link',
                        fontSize: '.9rem',
                        minWidth: '7.5rem',
                        maxWidth: '12rem',
                        padding: '0',
                        overflow: 'hidden',
                        position: 'relative',
                        verticalAlign: 'middle',
                        display: 'inline-block',
                        textDecoration: 'none',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textTransform: 'capitalize',
                      },
                    }}
                  >
                    {addTableBody.map((d) => (
                      <TableRow key={d.ticker ?? d.nameOfIssuer}>
                        {tbls.cols.map((c) => {
                          const value = d[c.field];
                          const issuerName = (d.nameOfIssuer ?? '').toLowerCase();
                          const isLinked =
                            !!d.ticker && safeTickers.some((e) => e?.t === d?.ticker);

                          return (
                            <TableCell
                              key={c.field}
                              sx={{
                                fontSize: '.9rem',
                                color:
                                  c.field !== 'ticker' && Math.sign(value) === 1
                                    ? '#0E8614'
                                    : c.field !== 'ticker'
                                    ? '#EB0000'
                                    : 'inherit',
                                fontWeight: c.field !== 'ticker' ? 600 : 500,
                                textAlign: c.field === 'ticker' ? 'left' : 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {c.field === 'ticker' ? (
                                isLinked ? (
                                  <LightTooltip title={d.nameOfIssuer ?? ''} disableInteractive>
                                    <Link
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="fund-field-ellipsis"
                                      underline="hover"
                                      href={`${dataConstants.NAVS.STOCK}/${d.ticker}`}
                                    >
                                      <Typography
                                        className="nameOfIssuer"
                                        variant="caption"
                                        sx={{ fontSize: '.9rem', textTransform: 'capitalize' }}
                                      >
                                        {issuerName}
                                      </Typography>
                                    </Link>
                                  </LightTooltip>
                                ) : (
                                  <LightTooltip title={d.nameOfIssuer ?? ''} disableInteractive>
                                    <Typography
                                      className="nameOfIssuer"
                                      variant="caption"
                                      sx={{
                                        color: '#333 !important',
                                        cursor: 'default !important',
                                        fontSize: '.9rem',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      {issuerName}
                                    </Typography>
                                  </LightTooltip>
                                )
                              ) : isNaN(value) ? (
                                value
                              ) : (
                                addCommas(c.float ? roundNum(value) : value)
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Box
                  component="div"
                  className="company-description align-items-end"
                  sx={{ mt: 0, p: 2, textAlign: 'center' }}
                >
                  <Typography
                    variant="caption"
                    component="div"
                    className="mb-0 company-nochange-message"
                    sx={{ color: 'text.very_light_desc' }}
                  >
                    {(tbls.message ?? '').replace('XXXXX', name ?? '')}
                  </Typography>
                </Box>
              )}
            </Box>
          </TableContainer>
          {tbls.desc && addTableBody && (
            <Box
              component="div"
              className="company-description align-items-end"
              sx={{ mt: 0, background: '#fbfbfc', borderRadius: '0 0 7px 7px' }}
            >
              <Divider />
              <Typography
                variant="caption"
                component="div"
                gutterBottom
                sx={{ fontWeight: 500, color: '#666', p: 1 }}
              >
                {tbls.desc.replace('XXXXX', name ?? '')}
              </Typography>
            </Box>
          )}
        </Item>
      );
    },
    [safeHoldings, safeTickers, name],
  );

  if (!Array.isArray(addTable) || addTable.length === 0 || safeHoldings.length === 0) {
    return null;
  }

  return (
    <Grid
      size={{ xs: 12, md: top_text && top_text?.answer ? 12 : 9 }}
      sx={{ height: 'auto' }}
    >
      {isMobile ? (
        <Box
          component="div"
          sx={{
            position: 'relative',
            '& .MuiTabs-root .MuiTabs-scrollButtons': {
              position: 'absolute',
              py: 0,
              opacity: 1,
              zIndex: 99,
              height: '48px',
            },
            '& .MuiTabs-root .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
            '& .MuiTabs-root .MuiTabScrollButton-root:first-of-type': {
              left: 0.33,
              pl: '5px',
              borderLeft: '1px solid #eae9ee',
              background: 'rgb(251 251 252)',
              justifyContent: 'flex-start',
              width: '37px',
              borderRadius: '7px 0 0 0',
            },
            '& .MuiTabs-root .MuiTabScrollButton-root:last-of-type': {
              right: 0,
              pr: '5px',
              borderRight: '1px solid #eae9ee',
              background: 'rgb(251 251 252)',
              justifyContent: 'flex-end',
              width: '37px',
              borderRadius: '0 7px 0 0',
            },
          }}
        >
          <AntTabs
            value={selectedTab}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="Holdings change categories"
          >
            {addTable.map((tbls) => (
              <AntTab
                key={tbls.filterType}
                label={tbls.tableName}
                id={`holdings-tab-${tbls.filterType}`}
                aria-controls={`holdings-tabpanel-${tbls.filterType}`}
              />
            ))}
          </AntTabs>
          {addTable.map((tbls, index) => (
            <Box
              key={tbls.filterType}
              role="tabpanel"
              hidden={selectedTab !== index}
              id={`holdings-tabpanel-${tbls.filterType}`}
              aria-labelledby={`holdings-tab-${tbls.filterType}`}
            >
              {selectedTab === index && renderTableContent(tbls)}
            </Box>
          ))}
        </Box>
      ) : (
        <Grid container spacing={1.2} sx={{ height: '100%' }}>
          {addTable.map((tbls) => (
            <Grid key={tbls.filterType} size={{ xs: 12, md: 3 }}>
              {renderTableContent(tbls)}
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};

export default BuyAddRedSold;